import { useState, useEffect } from 'react';
import { useAuthStore } from '../store';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import { toast } from 'sonner';
import { 
  Plus, RefreshCw, Trash2, Edit, Bot, Clock, Ban, 
  MessageSquare, Image as ImageIcon, Zap, Sun, Moon, Upload, X, 
  ListTree, ArrowRight, CornerDownRight, AlertTriangle
} from 'lucide-react';

export default function AutomationsPage() {
  const { user } = useAuthStore();
  const [automations, setAutomations] = useState([]);
  const [ignoredNumbers, setIgnoredNumbers] = useState([]);
  const [businessHours, setBusinessHours] = useState([]);
  const [connections, setConnections] = useState([]);
  const [images, setImages] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  
  // Modal states
  const [automationModalOpen, setAutomationModalOpen] = useState(false);
  const [editingAutomation, setEditingAutomation] = useState(null);
  const [hoursModalOpen, setHoursModalOpen] = useState(false);
  const [editingHours, setEditingHours] = useState(null);
  
  // Form states
  const [automationForm, setAutomationForm] = useState({
    name: '',
    connection_id: '',
    trigger_type: 'contains',
    trigger_text: '',
    response_message: '',
    response_image_id: '',
    delay_seconds: 3,
    active: true,
    is_menu: false,
    menu_options: [],
    parent_id: '',
    end_conversation: false,
    // Novos campos para mensagens interativas
    response_type: 'text',
    button_choices: [],
    button_links: {},
    list_button_text: 'Ver Opções',
    footer_text: '',
    // Cooldown anti-spam
    cooldown_hours: 0,
    // Teste automático Sigma
    sigma_test_url: '',
    sigma_test_plan_id: '',
    sigma_test_connection_id: '',
    sigma_test_hours: 4,
    sigma_test_limit: 1,
    sigma_test_limit_message: 'Você já atingiu o limite de testes disponíveis.',
    sigma_test_message: ''
  });
  
  // Estado para planos (para teste Sigma)
  const [plans, setPlans] = useState([]);
  const [sigmaIntegration, setSigmaIntegration] = useState(null);
  const [sigmaPackages, setSigmaPackages] = useState([]);
  
  const [ignoredForm, setIgnoredForm] = useState({
    phone: '',
    connection_id: '',
    reason: ''
  });
  
  const defaultSchedule = {
    "0": { enabled: false, start_time: "08:00", end_time: "18:00" },
    "1": { enabled: true, start_time: "08:00", end_time: "18:00" },
    "2": { enabled: true, start_time: "08:00", end_time: "18:00" },
    "3": { enabled: true, start_time: "08:00", end_time: "18:00" },
    "4": { enabled: true, start_time: "08:00", end_time: "18:00" },
    "5": { enabled: true, start_time: "08:00", end_time: "18:00" },
    "6": { enabled: false, start_time: "08:00", end_time: "18:00" },
  };
  
  const [hoursForm, setHoursForm] = useState({
    connection_id: '',
    enabled: false,
    schedule: { ...defaultSchedule },
    out_of_hours_message: 'Olá! No momento estamos fora do horário de atendimento. Retornaremos em breve! 🕐',
    welcome_message: 'Olá! Seja bem-vindo(a)! 👋 Como posso ajudá-lo(a)?',
    welcome_enabled: true
  });
  
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [automationConfigs, setAutomationConfigs] = useState([]);
  const [configModalOpen, setConfigModalOpen] = useState(false);
  const [configForm, setConfigForm] = useState({
    connection_id: '',
    reset_keywords: '',
    reset_message: ''
  });

  const dayNames = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
  const dayFullNames = ['Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado'];

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('nexus-token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const [automationsRes, ignoredRes, hoursRes, connectionsRes, imagesRes, statsRes, configsRes, customerPlansRes] = await Promise.all([
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/automations`, { headers }),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ignored-numbers`, { headers }),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/business-hours`, { headers }),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/connections?owner_filter=mine`, { headers }),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/images?category=automation`, { headers }),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/automations/stats`, { headers }),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/automation-config`, { headers }),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/customer-plans`, { headers })
      ]);
      
      if (automationsRes.ok) setAutomations(await automationsRes.json());
      if (ignoredRes.ok) setIgnoredNumbers(await ignoredRes.json());
      if (hoursRes.ok) setBusinessHours(await hoursRes.json());
      if (connectionsRes.ok) {
        const connectionsData = await connectionsRes.json();
        setConnections(connectionsData);
        
        // Auto-selecionar se tiver apenas 1 conexão
        const connectedConns = connectionsData.filter(c => c.status === 'connected');
        if (connectedConns.length === 1) {
          setAutomationForm(prev => ({ ...prev, connection_id: connectedConns[0].id }));
          setIgnoredForm(prev => ({ ...prev, connection_id: connectedConns[0].id }));
        }
      }
      if (imagesRes.ok) setImages(await imagesRes.json());
      if (statsRes.ok) setStats(await statsRes.json());
      if (configsRes.ok) setAutomationConfigs(await configsRes.json());
      if (customerPlansRes.ok) setPlans(await customerPlansRes.json());
      
      // Buscar integração Sigma para pacotes
      try {
        const sigmaRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/sigma/integration`, { headers });
        if (sigmaRes.ok) {
          const sigmaData = await sigmaRes.json();
          if (sigmaData?.enabled || sigmaData?.active) {
            setSigmaIntegration(sigmaData);
            // Buscar pacotes do Sigma
            const pkgRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/sigma/packages`, { headers });
            if (pkgRes.ok) {
              const pkgData = await pkgRes.json();
              setSigmaPackages(Array.isArray(pkgData) ? pkgData : []);
            }
          }
        }
      } catch (e) {
        console.log('Sigma integration not available');
      }
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const connectedConnections = connections.filter(c => c.status === 'connected');
  
  // Carregar preview da imagem
  const loadImagePreview = async (imageId) => {
    if (!imageId) {
      setImagePreview(null);
      return;
    }
    try {
      const token = localStorage.getItem('nexus-token');
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/images/${imageId}/file`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const blob = await res.blob();
        setImagePreview(URL.createObjectURL(blob));
      }
    } catch (e) {
      console.error('Erro ao carregar preview:', e);
    }
  };
  
  // Upload de imagem
  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Mostrar preview local
    const reader = new FileReader();
    reader.onloadend = () => setImagePreview(reader.result);
    reader.readAsDataURL(file);
    
    setUploading(true);
    try {
      const token = localStorage.getItem('nexus-token');
      const formData = new FormData();
      formData.append('file', file);
      formData.append('category', 'automation');
      
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/images`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });
      
      if (res.ok) {
        const newImage = await res.json();
        setImages(prev => [newImage, ...prev]);
        setAutomationForm(prev => ({ ...prev, response_image_id: newImage.id }));
        toast.success('Imagem enviada!');
      } else {
        toast.error('Erro ao enviar imagem');
        setImagePreview(null);
      }
    } catch (error) {
      toast.error('Erro ao enviar imagem');
      setImagePreview(null);
    } finally {
      setUploading(false);
    }
  };
  
  const clearImage = () => {
    setImagePreview(null);
    setAutomationForm(prev => ({ ...prev, response_image_id: '' }));
  };

  // Automation handlers
  const openAutomationModal = (automation = null) => {
    setImagePreview(null);
    if (automation) {
      setEditingAutomation(automation);
      setAutomationForm({
        name: automation.name,
        connection_id: automation.connection_id,
        trigger_type: automation.trigger_type,
        trigger_text: automation.trigger_text,
        response_message: automation.response_message,
        response_image_id: automation.response_image_id || '',
        delay_seconds: automation.delay_seconds,
        active: automation.active,
        is_menu: automation.is_menu || false,
        menu_options: automation.menu_options || [],
        parent_id: automation.parent_id || '',
        end_conversation: automation.end_conversation || false,
        // Novos campos interativos
        response_type: automation.response_type || 'text',
        button_choices: automation.button_choices || [],
        button_links: automation.button_links || {},
        list_button_text: automation.list_button_text || 'Ver Opções',
        footer_text: automation.footer_text || '',
        // Cooldown
        cooldown_hours: automation.cooldown_hours || 0,
        // Teste Sigma
        sigma_test_url: automation.sigma_test_url || '',
        sigma_test_plan_id: automation.sigma_test_plan_id || '',
        sigma_test_connection_id: automation.sigma_test_connection_id || '',
        sigma_test_hours: automation.sigma_test_hours || 4,
        sigma_test_limit: automation.sigma_test_limit || 1,
        sigma_test_limit_message: automation.sigma_test_limit_message || 'Você já atingiu o limite de testes disponíveis.',
        sigma_test_message: automation.sigma_test_message || ''
      });
      // Carregar preview se tiver imagem
      if (automation.response_image_id) {
        loadImagePreview(automation.response_image_id);
      }
    } else {
      setEditingAutomation(null);
      const firstConn = connectedConnections[0];
      setAutomationForm({
        name: '',
        connection_id: firstConn?.id || '',
        trigger_type: 'contains',
        trigger_text: '',
        response_message: '',
        response_image_id: '',
        delay_seconds: 3,
        active: true,
        is_menu: false,
        menu_options: [],
        parent_id: '',
        end_conversation: false,
        // Novos campos interativos
        response_type: 'text',
        button_choices: [],
        button_links: {},
        list_button_text: 'Ver Opções',
        footer_text: '',
        // Cooldown
        cooldown_hours: 0,
        // Teste Sigma
        sigma_test_url: '',
        sigma_test_plan_id: '',
        sigma_test_connection_id: '',
        sigma_test_hours: 4,
        sigma_test_limit: 1,
        sigma_test_limit_message: 'Você já atingiu o limite de testes disponíveis.',
        sigma_test_message: ''
      });
    }
    setAutomationModalOpen(true);
  };

  const saveAutomation = async () => {
    // Validação básica
    if (!automationForm.name || !automationForm.connection_id) {
      toast.error('Preencha o nome e selecione a conexão');
      return;
    }
    
    // Se for tipo sigma_test, não precisa de response_message obrigatória
    if (automationForm.response_type !== 'sigma_test' && !automationForm.response_message) {
      toast.error('Preencha a mensagem de resposta');
      return;
    }
    
    // Se for gatilho principal (sem parent), precisa de trigger_text
    if (!automationForm.parent_id && !automationForm.trigger_text) {
      toast.error('Preencha a palavra-chave (gatilho)');
      return;
    }
    
    // Se for parte de fluxo, precisa selecionar o pai
    if (automationForm.parent_id === 'select') {
      toast.error('Selecione o fluxo pai');
      return;
    }
    
    setSaving(true);
    try {
      const token = localStorage.getItem('nexus-token');
      const url = editingAutomation 
        ? `${process.env.REACT_APP_BACKEND_URL}/api/automations/${editingAutomation.id}`
        : `${process.env.REACT_APP_BACKEND_URL}/api/automations`;
      
      const body = { ...automationForm };
      if (!body.response_image_id) delete body.response_image_id;
      if (!body.parent_id || body.parent_id === 'select') {
        delete body.parent_id;
      } else {
        // Se é parte de fluxo, o trigger_text pode ser vazio
        body.trigger_text = body.trigger_text || `_subflow_${Date.now()}`;
      }
      if (!body.is_menu) body.menu_options = [];
      
      // Limpar campos de botões se não for tipo botão/lista
      if (body.response_type === 'text' || body.response_type === 'sigma_test') {
        body.button_choices = [];
        body.button_links = {};
        body.footer_text = '';
      }
      // Filtrar botões vazios e limpar links de botões removidos
      if (body.button_choices) {
        const validChoices = body.button_choices.filter(c => c && c.trim());
        const validLinks = {};
        validChoices.forEach(c => {
          if (body.button_links && body.button_links[c]) {
            validLinks[c] = body.button_links[c];
          }
        });
        body.button_choices = validChoices;
        body.button_links = validLinks;
      }
      
      // Garantir que campos do Sigma Test estão no body
      if (body.response_type === 'sigma_test') {
        body.sigma_test_url = body.sigma_test_url || '';
        body.sigma_test_plan_id = body.sigma_test_plan_id || '';
        body.sigma_test_connection_id = body.sigma_test_connection_id || '';
        body.sigma_test_limit = body.sigma_test_limit || 1;
        body.sigma_test_limit_message = body.sigma_test_limit_message || '';
        body.sigma_test_hours = body.sigma_test_hours || 4;
        body.sigma_test_message = body.sigma_test_message || '';
      }
      
      const res = await fetch(url, {
        method: editingAutomation ? 'PUT' : 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
      });
      
      if (res.ok) {
        toast.success(editingAutomation ? 'Automação atualizada!' : 'Automação criada!');
        setAutomationModalOpen(false);
        fetchData();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Erro ao salvar automação');
      }
    } catch (error) {
      toast.error('Erro ao salvar automação');
    } finally {
      setSaving(false);
    }
  };

  const deleteAutomation = async (automation) => {
    if (!confirm(`Excluir automação "${automation.name}"?`)) return;
    
    try {
      const token = localStorage.getItem('nexus-token');
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/automations/${automation.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        toast.success('Automação excluída');
        fetchData();
      } else {
        toast.error('Erro ao excluir automação');
      }
    } catch (error) {
      toast.error('Erro ao excluir automação');
    }
  };

  const toggleAutomation = async (automation) => {
    try {
      const token = localStorage.getItem('nexus-token');
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/automations/${automation.id}`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ active: !automation.active })
      });
      fetchData();
    } catch (error) {
      toast.error('Erro ao atualizar automação');
    }
  };

  // Menu Options handlers
  const addMenuOption = () => {
    const newKey = (automationForm.menu_options.length + 1).toString();
    setAutomationForm(prev => ({
      ...prev,
      menu_options: [...prev.menu_options, { key: newKey, label: '', next_automation_id: '' }]
    }));
  };

  const updateMenuOption = (index, field, value) => {
    setAutomationForm(prev => ({
      ...prev,
      menu_options: prev.menu_options.map((opt, i) => 
        i === index ? { ...opt, [field]: value } : opt
      )
    }));
  };

  const removeMenuOption = (index) => {
    setAutomationForm(prev => ({
      ...prev,
      menu_options: prev.menu_options.filter((_, i) => i !== index)
    }));
  };

  const addBackOption = () => {
    if (automationForm.menu_options.some(opt => opt.key === '0')) return;
    // Adicionar no FINAL da lista
    setAutomationForm(prev => ({
      ...prev,
      menu_options: [...prev.menu_options, { key: '0', label: 'Voltar ao menu anterior', next_automation_id: '_back_' }]
    }));
  };

  // Ignored Numbers handlers
  const addIgnoredNumber = async () => {
    if (!ignoredForm.phone || !ignoredForm.connection_id) {
      toast.error('Preencha o telefone e selecione a conexão');
      return;
    }
    
    setSaving(true);
    try {
      const token = localStorage.getItem('nexus-token');
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ignored-numbers`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(ignoredForm)
      });
      
      if (res.ok) {
        toast.success('Número adicionado à lista de ignorados');
        setIgnoredForm({ phone: '', connection_id: ignoredForm.connection_id, reason: '' });
        fetchData();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Erro ao adicionar número');
      }
    } catch (error) {
      toast.error('Erro ao adicionar número');
    } finally {
      setSaving(false);
    }
  };

  const removeIgnoredNumber = async (number) => {
    try {
      const token = localStorage.getItem('nexus-token');
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ignored-numbers/${number.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        toast.success('Número removido da lista');
        fetchData();
      }
    } catch (error) {
      toast.error('Erro ao remover número');
    }
  };

  // Business Hours handlers
  const openHoursModal = (config = null) => {
    if (config) {
      setEditingHours(config);
      setHoursForm({
        connection_id: config.connection_id,
        enabled: config.enabled,
        schedule: config.schedule || { ...defaultSchedule },
        out_of_hours_message: config.out_of_hours_message,
        welcome_message: config.welcome_message,
        welcome_enabled: config.welcome_enabled
      });
    } else {
      setEditingHours(null);
      const firstConn = connectedConnections[0];
      setHoursForm({
        connection_id: firstConn?.id || '',
        enabled: false,
        schedule: { ...defaultSchedule },
        out_of_hours_message: 'Olá! No momento estamos fora do horário de atendimento. Retornaremos em breve! 🕐',
        welcome_message: 'Olá! Seja bem-vindo(a)! 👋 Como posso ajudá-lo(a)?',
        welcome_enabled: true
      });
    }
    setHoursModalOpen(true);
  };

  const saveBusinessHours = async () => {
    if (!hoursForm.connection_id) {
      toast.error('Selecione uma conexão');
      return;
    }
    
    setSaving(true);
    try {
      const token = localStorage.getItem('nexus-token');
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/business-hours`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(hoursForm)
      });
      
      if (res.ok) {
        toast.success('Horário de serviço salvo!');
        setHoursModalOpen(false);
        fetchData();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Erro ao salvar');
      }
    } catch (error) {
      toast.error('Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  const deleteBusinessHours = async (config) => {
    if (!confirm('Excluir esta configuração de horário?')) return;
    
    try {
      const token = localStorage.getItem('nexus-token');
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/business-hours/${config.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        toast.success('Configuração excluída');
        fetchData();
      }
    } catch (error) {
      toast.error('Erro ao excluir');
    }
  };

  const updateDaySchedule = (day, field, value) => {
    setHoursForm(prev => ({
      ...prev,
      schedule: {
        ...prev.schedule,
        [day]: {
          ...prev.schedule[day],
          [field]: value
        }
      }
    }));
  };

  // Config Modal handlers
  const openConfigModal = (config = null) => {
    if (config) {
      setConfigForm({
        connection_id: config.connection_id,
        reset_keywords: config.reset_keywords || '',
        reset_message: config.reset_message || ''
      });
    } else {
      const firstConn = connectedConnections[0];
      setConfigForm({
        connection_id: firstConn?.id || '',
        reset_keywords: '',
        reset_message: ''
      });
    }
    setConfigModalOpen(true);
  };

  const saveConfig = async () => {
    if (!configForm.connection_id) {
      toast.error('Selecione uma conexão');
      return;
    }
    
    setSaving(true);
    try {
      const token = localStorage.getItem('nexus-token');
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/automation-config`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(configForm)
      });
      
      if (res.ok) {
        toast.success('Configuração salva!');
        setConfigModalOpen(false);
        fetchData();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Erro ao salvar');
      }
    } catch (error) {
      toast.error('Erro ao salvar configuração');
    } finally {
      setSaving(false);
    }
  };

  // Filtrar automações principais (sem parent) e submenus
  const mainAutomations = automations.filter(a => !a.parent_id);
  const subAutomations = automations.filter(a => a.parent_id);
  const getSubAutomations = (parentId) => automations.filter(a => a.parent_id === parentId);
  const getParentName = (parentId) => {
    const parent = automations.find(a => a.id === parentId);
    return parent?.name || 'Desconhecido';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Bot className="w-6 h-6 text-primary" />
            Automações
            <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-yellow-500/20 text-yellow-600 dark:text-yellow-400 uppercase tracking-wide">
              Beta
            </span>
          </h1>
          <p className="text-muted-foreground text-sm">Respostas automáticas, fluxos e menus interativos</p>
        </div>
      </div>

      {/* Beta Warning */}
      <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-yellow-500/10 border border-yellow-500/20 text-yellow-700 dark:text-yellow-400 text-sm">
        <AlertTriangle className="w-4 h-4 flex-shrink-0" />
        <span>Este módulo está em fase beta. Algumas funcionalidades podem apresentar instabilidades.</span>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card>
          <CardContent className="p-3 md:p-4">
            <div className="flex items-center gap-2">
              <Zap className="w-5 h-5 text-yellow-500" />
              <div>
                <p className="text-lg md:text-2xl font-bold">{stats.active_automations || 0}</p>
                <p className="text-xs text-muted-foreground">Automações Ativas</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3 md:p-4">
            <div className="flex items-center gap-2">
              <ListTree className="w-5 h-5 text-blue-500" />
              <div>
                <p className="text-lg md:text-2xl font-bold">{automations.filter(a => a.is_menu).length}</p>
                <p className="text-xs text-muted-foreground">Menus/Fluxos</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3 md:p-4">
            <div className="flex items-center gap-2">
              <Ban className="w-5 h-5 text-red-500" />
              <div>
                <p className="text-lg md:text-2xl font-bold">{stats.ignored_numbers || 0}</p>
                <p className="text-xs text-muted-foreground">Números Ignorados</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3 md:p-4">
            <div className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-green-500" />
              <div>
                <p className="text-lg md:text-2xl font-bold">{stats.business_hours_configs || 0}</p>
                <p className="text-xs text-muted-foreground">Horários Config.</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="automations" className="w-full">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="automations" className="flex items-center gap-1">
            <MessageSquare className="w-4 h-4" />
            <span className="hidden sm:inline">Respostas</span>
          </TabsTrigger>
          <TabsTrigger value="ignored" className="flex items-center gap-1">
            <Ban className="w-4 h-4" />
            <span className="hidden sm:inline">Ignorados</span>
          </TabsTrigger>
          <TabsTrigger value="hours" className="flex items-center gap-1">
            <Clock className="w-4 h-4" />
            <span className="hidden sm:inline">Horário</span>
          </TabsTrigger>
        </TabsList>

        {/* Tab: Automations */}
        <TabsContent value="automations" className="mt-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-lg">Respostas Automáticas</CardTitle>
                <CardDescription>Configure respostas e menus interativos</CardDescription>
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => setConfigModalOpen(true)} disabled={connectedConnections.length === 0}>
                  <Zap className="w-4 h-4 mr-1" />
                  Reset Fluxo
                </Button>
                <Button size="sm" onClick={() => openAutomationModal()} disabled={connectedConnections.length === 0}>
                  <Plus className="w-4 h-4 mr-1" />
                  Nova
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {connectedConnections.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Bot className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>Conecte um WhatsApp primeiro</p>
                </div>
              ) : automations.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Bot className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>Nenhuma automação configurada</p>
                  <Button variant="link" onClick={() => openAutomationModal()}>
                    Criar primeira automação
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  {/* Automações principais (com gatilho) */}
                  {mainAutomations.map(auto => (
                    <div key={auto.id} className="border rounded-lg overflow-hidden">
                      <div className="flex items-center justify-between p-3 bg-muted/50">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h4 className="font-medium">{auto.name}</h4>
                            <Badge variant={auto.active ? 'default' : 'secondary'} className="text-xs">
                              {auto.active ? 'Ativo' : 'Inativo'}
                            </Badge>
                            {auto.is_menu && (
                              <Badge variant="outline" className="text-xs bg-purple-500/10 text-purple-400">
                                <ListTree className="w-3 h-3 mr-1" />
                                Menu
                              </Badge>
                            )}
                            {auto.response_image_id && (
                              <Badge variant="outline" className="text-xs">
                                <ImageIcon className="w-3 h-3 mr-1" />
                                Imagem
                              </Badge>
                            )}
                          </div>
                          <div className="text-sm text-muted-foreground mt-1">
                            {auto.trigger_type === 'any' ? (
                              <span className="inline-flex items-center gap-1 text-yellow-600 dark:text-yellow-400">
                                <Zap className="w-3 h-3" />
                                Qualquer mensagem
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1">
                                <span className="font-mono bg-primary/10 px-1 rounded">{auto.trigger_type === 'exact' ? '=' : '~'}</span>
                                &quot;{auto.trigger_text}&quot;
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-muted-foreground mt-1">
                            {auto.connection_name} • Delay: {auto.delay_seconds}s
                            {auto.menu_options?.length > 0 && ` • ${auto.menu_options.length} opções`}
                            {auto.cooldown_hours > 0 && (
                              <span className="ml-2 text-orange-500">• Cooldown: {auto.cooldown_hours}h</span>
                            )}
                            {auto.response_type === 'sigma_test' && (
                              <span className="ml-2 text-purple-500">• 🎬 Teste Sigma</span>
                            )}
                          </p>
                        </div>
                        <div className="flex items-center gap-2 ml-3">
                          <Switch 
                            checked={auto.active} 
                            onCheckedChange={() => toggleAutomation(auto)}
                          />
                          <Button size="sm" variant="ghost" onClick={() => openAutomationModal(auto)}>
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button size="sm" variant="ghost" className="text-red-500" onClick={() => deleteAutomation(auto)}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                      
                      {/* Sub-automações */}
                      {auto.is_menu && auto.menu_options?.length > 0 && (
                        <div className="p-2 bg-muted/20 border-t">
                          <p className="text-xs text-muted-foreground mb-2 px-2">Opções do menu:</p>
                          <div className="space-y-1">
                            {auto.menu_options.map((opt, idx) => {
                              const nextAuto = opt.next_automation_id === '_back_' ? null : automations.find(a => a.id === opt.next_automation_id);
                              return (
                                <div key={idx} className="flex items-center gap-2 px-2 py-1 text-sm">
                                  <Badge variant="outline" className="w-6 h-6 flex items-center justify-center p-0">
                                    {opt.key}
                                  </Badge>
                                  <span className="text-muted-foreground">{opt.label}</span>
                                  {opt.next_automation_id === '_back_' ? (
                                    <span className="text-xs text-blue-400">↩ Volta ao menu pai</span>
                                  ) : nextAuto && (
                                    <>
                                      <ArrowRight className="w-3 h-3 text-muted-foreground" />
                                      <span className="text-xs bg-muted px-2 py-0.5 rounded">{nextAuto.name}</span>
                                    </>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                  
                  {/* Subfluxos (parte de outro menu) */}
                  {subAutomations.length > 0 && (
                    <div className="pt-4 border-t">
                      <p className="text-sm font-medium text-muted-foreground mb-3 flex items-center gap-2">
                        <CornerDownRight className="w-4 h-4" />
                        Subfluxos (parte de menus)
                      </p>
                      <div className="space-y-2">
                        {subAutomations.map(auto => (
                          <div key={auto.id} className="flex items-center justify-between p-3 rounded-lg bg-muted/30 border border-dashed">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <CornerDownRight className="w-4 h-4 text-muted-foreground" />
                                <h4 className="font-medium">{auto.name}</h4>
                                <Badge variant={auto.active ? 'default' : 'secondary'} className="text-xs">
                                  {auto.active ? 'Ativo' : 'Inativo'}
                                </Badge>
                                {auto.is_menu && (
                                  <Badge variant="outline" className="text-xs bg-purple-500/10 text-purple-400">
                                    <ListTree className="w-3 h-3 mr-1" />
                                    Menu
                                  </Badge>
                                )}
                              </div>
                              <p className="text-xs text-muted-foreground mt-1">
                                Parte de: <span className="font-medium">{getParentName(auto.parent_id)}</span>
                              </p>
                            </div>
                            <div className="flex items-center gap-2 ml-3">
                              <Switch 
                                checked={auto.active} 
                                onCheckedChange={() => toggleAutomation(auto)}
                              />
                              <Button size="sm" variant="ghost" onClick={() => openAutomationModal(auto)}>
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button size="sm" variant="ghost" className="text-red-500" onClick={() => deleteAutomation(auto)}>
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab: Ignored Numbers */}
        <TabsContent value="ignored" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Números Ignorados</CardTitle>
              <CardDescription>Números que não receberão respostas automáticas</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {connectedConnections.length > 0 && (
                <div className="flex flex-col sm:flex-row gap-3 p-4 rounded-lg bg-muted/50">
                  <Input
                    placeholder="5521999999999"
                    value={ignoredForm.phone}
                    onChange={(e) => setIgnoredForm({ ...ignoredForm, phone: e.target.value })}
                    className="flex-1"
                  />
                  <Select 
                    value={ignoredForm.connection_id || 'select'} 
                    onValueChange={(v) => v !== 'select' && setIgnoredForm({ ...ignoredForm, connection_id: v })}
                  >
                    <SelectTrigger className="w-full sm:w-[180px]">
                      <SelectValue placeholder="Conexão" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="select" disabled>Selecione...</SelectItem>
                      {connectedConnections.map(conn => (
                        <SelectItem key={conn.id} value={conn.id}>{conn.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input
                    placeholder="Motivo (opcional)"
                    value={ignoredForm.reason}
                    onChange={(e) => setIgnoredForm({ ...ignoredForm, reason: e.target.value })}
                    className="flex-1"
                  />
                  <Button onClick={addIgnoredNumber} disabled={saving || !ignoredForm.phone || !ignoredForm.connection_id}>
                    <Plus className="w-4 h-4 mr-1" />
                    Adicionar
                  </Button>
                </div>
              )}

              {ignoredNumbers.length === 0 ? (
                <div className="text-center py-6 text-muted-foreground">
                  <Ban className="w-10 h-10 mx-auto mb-2 opacity-50" />
                  <p>Nenhum número na lista</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {ignoredNumbers.map(num => (
                    <div key={num.id} className="flex items-center justify-between p-3 rounded bg-muted/30">
                      <div>
                        <p className="font-mono font-medium">{num.phone}</p>
                        <p className="text-xs text-muted-foreground">
                          {num.connection_name}
                          {num.reason && ` • ${num.reason}`}
                        </p>
                      </div>
                      <Button size="sm" variant="ghost" className="text-red-500" onClick={() => removeIgnoredNumber(num)}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab: Business Hours */}
        <TabsContent value="hours" className="mt-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-lg">Horário de Serviço</CardTitle>
                <CardDescription>Configure horário de atendimento e mensagens automáticas</CardDescription>
              </div>
              <Button size="sm" onClick={() => openHoursModal()} disabled={connectedConnections.length === 0}>
                <Plus className="w-4 h-4 mr-1" />
                Configurar
              </Button>
            </CardHeader>
            <CardContent>
              {connectedConnections.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Clock className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>Conecte um WhatsApp primeiro</p>
                </div>
              ) : businessHours.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Clock className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>Nenhum horário configurado</p>
                  <Button variant="link" onClick={() => openHoursModal()}>
                    Configurar horário de atendimento
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  {businessHours.map(config => (
                    <div key={config.id} className="p-4 rounded-lg bg-muted/50 border">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <h4 className="font-medium">{config.connection_name}</h4>
                          <Badge variant={config.enabled ? 'default' : 'secondary'}>
                            {config.enabled ? 'Ativo' : 'Inativo'}
                          </Badge>
                        </div>
                        <div className="flex gap-2">
                          <Button size="sm" variant="ghost" onClick={() => openHoursModal(config)}>
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button size="sm" variant="ghost" className="text-red-500" onClick={() => deleteBusinessHours(config)}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                      
                      <div className="space-y-1 text-sm mb-3">
                        {config.schedule && Object.entries(config.schedule).map(([day, sched]) => (
                          sched.enabled && (
                            <div key={day} className="flex items-center gap-2 text-muted-foreground">
                              <span className="w-16 font-medium">{dayFullNames[parseInt(day)]}:</span>
                              <span>{sched.start_time} - {sched.end_time}</span>
                            </div>
                          )
                        ))}
                      </div>
                      
                      <div className="pt-3 border-t space-y-2">
                        <div className="flex items-start gap-2">
                          <Moon className="w-4 h-4 text-blue-500 mt-0.5" />
                          <div>
                            <p className="text-xs text-muted-foreground">Fora do horário:</p>
                            <p className="text-sm">{config.out_of_hours_message?.substring(0, 80)}...</p>
                          </div>
                        </div>
                        {config.welcome_enabled && (
                          <div className="flex items-start gap-2">
                            <Sun className="w-4 h-4 text-yellow-500 mt-0.5" />
                            <div>
                              <p className="text-xs text-muted-foreground">Boas-vindas:</p>
                              <p className="text-sm">{config.welcome_message?.substring(0, 80)}...</p>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Automation Modal */}
      <Dialog open={automationModalOpen} onOpenChange={setAutomationModalOpen}>
        <DialogContent className="max-w-2xl w-[95vw] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingAutomation ? 'Editar Automação' : 'Nova Automação'}</DialogTitle>
            <DialogDescription>
              Configure uma resposta automática ou menu interativo
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Nome *</Label>
                <Input
                  placeholder="Ex: Menu Principal, Preços..."
                  value={automationForm.name}
                  onChange={(e) => setAutomationForm({ ...automationForm, name: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Conexão WhatsApp *</Label>
                <Select 
                  value={automationForm.connection_id || 'select'} 
                  onValueChange={(v) => v !== 'select' && setAutomationForm({ ...automationForm, connection_id: v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="select" disabled>Selecione...</SelectItem>
                    {connectedConnections.map(conn => (
                      <SelectItem key={conn.id} value={conn.id}>{conn.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {/* Tipo: Gatilho ou Parte de Fluxo */}
            <div className="space-y-3 p-3 rounded-lg bg-muted/30 border">
              <Label className="text-base">Tipo de Ativação</Label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => setAutomationForm({ ...automationForm, parent_id: '' })}
                  className={`p-3 rounded-lg border-2 text-left transition-colors ${
                    !automationForm.parent_id 
                      ? 'border-primary bg-primary/10' 
                      : 'border-border hover:border-muted-foreground'
                  }`}
                >
                  <p className="font-medium">Gatilho Principal</p>
                  <p className="text-xs text-muted-foreground">Ativado por palavra-chave</p>
                </button>
                <button
                  type="button"
                  onClick={() => setAutomationForm({ ...automationForm, parent_id: 'select', trigger_text: '' })}
                  className={`p-3 rounded-lg border-2 text-left transition-colors ${
                    automationForm.parent_id 
                      ? 'border-primary bg-primary/10' 
                      : 'border-border hover:border-muted-foreground'
                  }`}
                >
                  <p className="font-medium flex items-center gap-2">
                    <CornerDownRight className="w-4 h-4" />
                    Parte de um Fluxo
                  </p>
                  <p className="text-xs text-muted-foreground">Resposta de outra automação</p>
                </button>
              </div>
              
              {/* Se for parte de fluxo, mostrar seletor */}
              {automationForm.parent_id && (
                <div className="space-y-2 pt-2">
                  <Label>Selecione o fluxo pai</Label>
                  <Select 
                    value={automationForm.parent_id || 'select'} 
                    onValueChange={(v) => v !== 'select' && setAutomationForm({ ...automationForm, parent_id: v })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione a automação pai..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="select" disabled>Selecione...</SelectItem>
                      {automations
                        .filter(a => !a.parent_id && a.id !== editingAutomation?.id)
                        .map(a => (
                          <SelectItem key={a.id} value={a.id}>
                            {a.name} {a.is_menu ? `(${a.menu_options?.length || 0} opções)` : ''}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    Após criar, vincule esta automação a uma opção do menu pai
                  </p>
                </div>
              )}
            </div>
            
            {/* Gatilho - só mostra se não for parte de fluxo */}
            {!automationForm.parent_id && (
              <div className="space-y-3">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label>Tipo de Gatilho</Label>
                    <Select 
                      value={automationForm.trigger_type} 
                      onValueChange={(v) => setAutomationForm({ ...automationForm, trigger_type: v, trigger_text: v === 'any' ? '_any_' : automationForm.trigger_text })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="exact">Exato (=)</SelectItem>
                        <SelectItem value="contains">Contém (~)</SelectItem>
                        <SelectItem value="any">Qualquer mensagem</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Delay (seg)</Label>
                    <Input
                      type="number"
                      min="0"
                      max="30"
                      value={automationForm.delay_seconds}
                      onChange={(e) => setAutomationForm({ ...automationForm, delay_seconds: parseInt(e.target.value) || 0 })}
                    />
                  </div>
                </div>
                
                {/* Palavra-chave - só mostra se não for "qualquer mensagem" */}
                {automationForm.trigger_type !== 'any' && (
                  <div className="space-y-2">
                    <Label>Palavra-chave(s) *</Label>
                    <Input
                      placeholder="oi, olá, menu, preço (separe por vírgula)"
                      value={automationForm.trigger_text}
                      onChange={(e) => setAutomationForm({ ...automationForm, trigger_text: e.target.value })}
                    />
                    <p className="text-xs text-muted-foreground">
                      Pode adicionar várias palavras separadas por vírgula. Cada uma será verificada individualmente.
                    </p>
                  </div>
                )}
                
                {automationForm.trigger_type === 'any' && (
                  <div className="p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/30">
                    <p className="text-sm text-yellow-600 dark:text-yellow-400">
                      ⚠️ Esta automação responderá a <strong>qualquer mensagem</strong> recebida.
                      Ela não interrompe fluxos já em andamento.
                    </p>
                  </div>
                )}
              </div>
            )}
            
            {/* Delay se for parte de fluxo */}
            {automationForm.parent_id && (
              <div className="space-y-2">
                <Label>Delay (segundos)</Label>
                <Input
                  type="number"
                  min="0"
                  max="30"
                  value={automationForm.delay_seconds}
                  onChange={(e) => setAutomationForm({ ...automationForm, delay_seconds: parseInt(e.target.value) || 0 })}
                  className="w-32"
                />
              </div>
            )}
            
            {/* Cooldown Anti-Spam */}
            <div className="space-y-3 p-4 rounded-lg bg-gradient-to-r from-orange-500/10 to-red-500/10 border border-orange-500/20">
              <Label className="flex items-center gap-2 text-base font-semibold">
                <Clock className="w-4 h-4 text-orange-500" />
                Cooldown (Anti-Spam)
              </Label>
              <div className="flex items-center gap-4">
                <div className="flex-1 space-y-2">
                  <Input
                    type="number"
                    min="0"
                    max="168"
                    placeholder="0 = desativado"
                    value={automationForm.cooldown_hours || 0}
                    onChange={(e) => setAutomationForm({ ...automationForm, cooldown_hours: parseInt(e.target.value) || 0 })}
                    className="w-32"
                  />
                  <p className="text-xs text-muted-foreground">
                    Horas até responder novamente ao mesmo número. 0 = sempre responde.
                  </p>
                </div>
                {automationForm.cooldown_hours > 0 && (
                  <Badge variant="outline" className="bg-orange-500/20 border-orange-500/50">
                    Pausa de {automationForm.cooldown_hours}h
                  </Badge>
                )}
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>
                {automationForm.response_type === 'sigma_test' 
                  ? 'Mensagem de Erro (quando Sigma falhar)' 
                  : 'Mensagem de Resposta *'}
              </Label>
              <Textarea
                rows={4}
                className="min-h-[100px] text-base"
                placeholder={automationForm.response_type === 'sigma_test'
                  ? "Mensagem enviada se a Sigma não responder..."
                  : "Digite a mensagem que será enviada automaticamente..."}
                value={automationForm.response_message}
                onChange={(e) => setAutomationForm({ ...automationForm, response_message: e.target.value })}
              />
              {automationForm.response_type === 'sigma_test' && (
                <p className="text-xs text-muted-foreground">
                  Esta mensagem só é enviada se houver erro ao buscar o teste na Sigma.
                </p>
              )}
            </div>
            
            {/* Tipo de Resposta */}
            <div className="space-y-3 p-4 rounded-lg bg-gradient-to-r from-blue-500/10 to-purple-500/10 border border-blue-500/20">
              <Label className="flex items-center gap-2 text-base font-semibold">
                <Zap className="w-4 h-4 text-blue-500" />
                Tipo de Resposta
              </Label>
              <Select
                value={automationForm.response_type}
                onValueChange={(v) => {
                  const updates = { response_type: v };
                  // Quando selecionar Teste Sigma, preencher template automático
                  if (v === 'sigma_test' && !automationForm.response_message) {
                    updates.response_message = '⚠️ Desculpe, não foi possível gerar seu teste no momento. Tente novamente mais tarde.';
                    updates.sigma_test_limit_message = '❌ Você já utilizou seu teste gratuito.\n\n📱 Para adquirir um plano, acesse nosso site ou fale com um atendente!';
                  }
                  setAutomationForm({ ...automationForm, ...updates });
                }}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="text">📝 Texto Simples</SelectItem>
                  <SelectItem value="sigma_test">🎬 Teste Automático Sigma</SelectItem>
                </SelectContent>
              </Select>
              
              {/* Configuração de Teste Sigma */}
              {automationForm.response_type === 'sigma_test' && (
                <div className="space-y-4 pt-3 border-t border-blue-500/20">
                  <div className="p-3 rounded-lg bg-green-500/10 border border-green-500/30">
                    <p className="text-sm text-green-700 dark:text-green-300 font-medium mb-2">
                      ✅ A resposta do teste é enviada automaticamente pela Sigma!
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Quando o cliente acionar esta automação, o sistema busca um teste na Sigma e envia a mensagem completa com usuário, senha, links e data de vencimento.
                    </p>
                  </div>
                  
                  <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/30">
                    <p className="text-sm text-amber-700 dark:text-amber-300 flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4" />
                      O cliente será cadastrado automaticamente no Gestor de Clientes.
                    </p>
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">URL do Chatbot Sigma *</Label>
                    <Input
                      placeholder="https://seupainel.com/api/chatbot/xxx/yyy"
                      value={automationForm.sigma_test_url || ''}
                      onChange={(e) => setAutomationForm({ ...automationForm, sigma_test_url: e.target.value })}
                    />
                    <p className="text-xs text-muted-foreground">
                      URL da API de teste automático do seu painel Sigma
                    </p>
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Pacote Sigma para Teste *</Label>
                    <Select
                      value={automationForm.sigma_test_plan_id || ''}
                      onValueChange={(v) => setAutomationForm({ ...automationForm, sigma_test_plan_id: v })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione o pacote Sigma" />
                      </SelectTrigger>
                      <SelectContent>
                        {sigmaPackages.length > 0 ? (
                          sigmaPackages.map(pkg => (
                            <SelectItem key={pkg.id} value={String(pkg.id)}>
                              🎬 {pkg.name} {pkg.price ? `- R$ ${pkg.price}` : ''}
                            </SelectItem>
                          ))
                        ) : (
                          <SelectItem value="_empty" disabled>
                            {sigmaIntegration ? 'Nenhum pacote encontrado' : 'Integração Sigma não configurada'}
                          </SelectItem>
                        )}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">
                      Pacote do Sigma usado ao cadastrar o cliente de teste
                    </p>
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Conexão para Cobranças</Label>
                    <Select
                      value={automationForm.sigma_test_connection_id || ''}
                      onValueChange={(v) => setAutomationForm({ ...automationForm, sigma_test_connection_id: v })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione a conexão" />
                      </SelectTrigger>
                      <SelectContent>
                        {connectedConnections.map(conn => (
                          <SelectItem key={conn.id} value={conn.id}>
                            📱 {conn.name || conn.instance_name}
                          </SelectItem>
                        ))}
                        {connectedConnections.length === 0 && (
                          <SelectItem value="_empty" disabled>
                            Nenhuma conexão disponível
                          </SelectItem>
                        )}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">
                      Conexão usada para enviar mensagens de cobrança ao cliente
                    </p>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">Duração do Teste (horas)</Label>
                      <Input
                        type="number"
                        min="1"
                        max="720"
                        value={automationForm.sigma_test_hours || 4}
                        onChange={(e) => setAutomationForm({ ...automationForm, sigma_test_hours: parseInt(e.target.value) || 4 })}
                      />
                      <p className="text-xs text-muted-foreground">
                        Tempo de validade do teste (ex: 4h, 24h, 48h)
                      </p>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">Limite de Testes</Label>
                      <Input
                        type="number"
                        min="1"
                        max="10"
                        value={automationForm.sigma_test_limit || 1}
                        onChange={(e) => setAutomationForm({ ...automationForm, sigma_test_limit: parseInt(e.target.value) || 1 })}
                      />
                      <p className="text-xs text-muted-foreground">
                        Máx. de testes por número
                      </p>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Mensagem Personalizada do Teste</Label>
                    <Textarea
                      placeholder="🎬 *Seu teste está pronto!*

👤 *Usuário:* {usuario}
🔑 *Senha:* {senha}
🌐 *Servidor:* {dns}
📦 *Pacote:* {pacote}
⏰ *Validade:* {vencimento}

Aproveite! 🚀"
                      value={automationForm.sigma_test_message || ''}
                      onChange={(e) => setAutomationForm({ ...automationForm, sigma_test_message: e.target.value })}
                      rows={8}
                      className="font-mono text-sm"
                    />
                    <div className="p-2 rounded bg-muted/50 text-xs space-y-1">
                      <p className="font-medium">Variáveis disponíveis:</p>
                      <div className="grid grid-cols-2 gap-1 text-muted-foreground">
                        <span><code>{'{usuario}'}</code> - Nome de usuário</span>
                        <span><code>{'{senha}'}</code> - Senha</span>
                        <span><code>{'{dns}'}</code> ou <code>{'{servidor}'}</code> - URL do servidor</span>
                        <span><code>{'{pacote}'}</code> ou <code>{'{plano}'}</code> - Nome do pacote</span>
                        <span><code>{'{vencimento}'}</code> ou <code>{'{expiracao}'}</code> - Data/hora</span>
                      </div>
                      <p className="text-amber-600 mt-2">💡 Deixe vazio para usar a mensagem padrão da Sigma</p>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Mensagem de Limite</Label>
                    <Textarea
                      placeholder="Você já atingiu o limite de testes disponíveis."
                      value={automationForm.sigma_test_limit_message || ''}
                      onChange={(e) => setAutomationForm({ ...automationForm, sigma_test_limit_message: e.target.value })}
                      rows={2}
                    />
                    <p className="text-xs text-muted-foreground">
                      Mensagem enviada quando o cliente atinge o limite de testes
                    </p>
                  </div>
                </div>
              )}
              
            </div>
            
            {/* Imagem */}
            <div className="space-y-2">
              <Label>Imagem (opcional)</Label>
              <div className="flex gap-3">
                {/* Preview */}
                {imagePreview && (
                  <div className="relative w-20 h-20 rounded border overflow-hidden flex-shrink-0">
                    <img src={imagePreview} alt="Preview" className="w-full h-full object-cover" />
                    <button
                      type="button"
                      onClick={clearImage}
                      className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-0.5"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                )}
                
                <div className="flex-1 space-y-2">
                  {/* Upload */}
                  <div className="relative">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                      disabled={uploading}
                    />
                    <Button variant="outline" size="sm" className="w-full" disabled={uploading}>
                      {uploading ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                      {uploading ? 'Enviando...' : 'Upload'}
                    </Button>
                  </div>
                  
                  {/* Select existing */}
                  {!imagePreview && (
                    <Select 
                      value={automationForm.response_image_id || 'none'} 
                      onValueChange={(v) => {
                        const newId = v === 'none' ? '' : v;
                        setAutomationForm({ ...automationForm, response_image_id: newId });
                        loadImagePreview(newId);
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Ou selecione existente" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Sem imagem</SelectItem>
                        {images.map(img => (
                          <SelectItem key={img.id} value={img.id}>{img.original_name || img.filename}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </div>
              </div>
            </div>
            
            {/* Menu Options */}
            <div className="space-y-3 p-4 rounded-lg bg-muted/50">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Switch
                    checked={automationForm.is_menu}
                    onCheckedChange={(checked) => setAutomationForm({ ...automationForm, is_menu: checked })}
                  />
                  <Label className="flex items-center gap-2">
                    <ListTree className="w-4 h-4 text-purple-500" />
                    Menu com opções
                  </Label>
                </div>
              </div>
              
              {automationForm.is_menu && (
                <div className="space-y-3 pt-3 border-t">
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-muted-foreground">Opções do menu</p>
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" onClick={addBackOption}>
                        <CornerDownRight className="w-3 h-3 mr-1" />
                        Voltar (0)
                      </Button>
                      <Button size="sm" variant="outline" onClick={addMenuOption}>
                        <Plus className="w-3 h-3 mr-1" />
                        Opção
                      </Button>
                    </div>
                  </div>
                  
                  {automationForm.menu_options.map((opt, index) => (
                    <div key={index} className="flex flex-col sm:flex-row sm:items-center gap-2 p-3 rounded bg-background border">
                      <div className="flex items-center gap-2">
                        <Input
                          className="w-16 text-center text-base"
                          value={opt.key}
                          onChange={(e) => updateMenuOption(index, 'key', e.target.value)}
                          placeholder="#"
                        />
                        <span className="text-muted-foreground hidden sm:inline">-</span>
                      </div>
                      <Input
                        className="flex-1 text-base min-h-[44px]"
                        value={opt.label}
                        onChange={(e) => updateMenuOption(index, 'label', e.target.value)}
                        placeholder="Descrição da opção (ex: Ver preços)"
                      />
                      <div className="flex items-center gap-2">
                        <Select 
                          value={opt.next_automation_id || 'none'} 
                          onValueChange={(v) => updateMenuOption(index, 'next_automation_id', v === 'none' ? '' : v)}
                        >
                          <SelectTrigger className="w-full sm:w-[160px]">
                            <SelectValue placeholder="Próxima..." />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">Fim do fluxo</SelectItem>
                            {automations.filter(a => a.id !== editingAutomation?.id).map(a => (
                              <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Button size="sm" variant="ghost" className="text-red-500 flex-shrink-0" onClick={() => removeMenuOption(index)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                  
                  {automationForm.menu_options.length === 0 && (
                    <p className="text-sm text-muted-foreground text-center py-2">
                      Adicione opções para criar o menu
                    </p>
                  )}
                </div>
              )}
            </div>
            
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Switch
                  checked={automationForm.active}
                  onCheckedChange={(checked) => setAutomationForm({ ...automationForm, active: checked })}
                />
                <Label>Ativar</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={automationForm.end_conversation}
                  onCheckedChange={(checked) => setAutomationForm({ ...automationForm, end_conversation: checked })}
                />
                <Label className="text-muted-foreground">Não esperar resposta</Label>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setAutomationModalOpen(false)}>Cancelar</Button>
            <Button onClick={saveAutomation} disabled={saving}>
              {saving ? 'Salvando...' : (editingAutomation ? 'Salvar' : 'Criar')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Business Hours Modal */}
      <Dialog open={hoursModalOpen} onOpenChange={setHoursModalOpen}>
        <DialogContent className="max-w-2xl w-[95vw] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Horário de Serviço</DialogTitle>
            <DialogDescription>
              Configure o horário de atendimento para cada dia da semana
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Conexão WhatsApp *</Label>
              <Select 
                value={hoursForm.connection_id || 'select'} 
                onValueChange={(v) => v !== 'select' && setHoursForm({ ...hoursForm, connection_id: v })}
                disabled={editingHours}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecione a conexão" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="select" disabled>Selecione...</SelectItem>
                  {connectedConnections.map(conn => (
                    <SelectItem key={conn.id} value={conn.id}>{conn.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex items-center gap-2 p-3 rounded bg-muted/50">
              <Switch
                checked={hoursForm.enabled}
                onCheckedChange={(checked) => setHoursForm({ ...hoursForm, enabled: checked })}
              />
              <Label>Ativar controle de horário</Label>
            </div>
            
            <div className="space-y-3">
              <Label className="text-base">Horários por Dia</Label>
              <div className="space-y-2">
                {[0, 1, 2, 3, 4, 5, 6].map(day => (
                  <div key={day} className="flex items-center gap-3 p-2 rounded bg-muted/30">
                    <div className="w-20">
                      <Switch
                        checked={hoursForm.schedule[day]?.enabled || false}
                        onCheckedChange={(checked) => updateDaySchedule(day.toString(), 'enabled', checked)}
                      />
                    </div>
                    <span className="w-20 font-medium text-sm">{dayFullNames[day]}</span>
                    <Input
                      type="time"
                      value={hoursForm.schedule[day]?.start_time || '08:00'}
                      onChange={(e) => updateDaySchedule(day.toString(), 'start_time', e.target.value)}
                      className="w-28"
                      disabled={!hoursForm.schedule[day]?.enabled}
                    />
                    <span className="text-muted-foreground">até</span>
                    <Input
                      type="time"
                      value={hoursForm.schedule[day]?.end_time || '18:00'}
                      onChange={(e) => updateDaySchedule(day.toString(), 'end_time', e.target.value)}
                      className="w-28"
                      disabled={!hoursForm.schedule[day]?.enabled}
                    />
                  </div>
                ))}
              </div>
            </div>
            
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Moon className="w-4 h-4 text-blue-500" />
                Mensagem Fora do Horário
              </Label>
              <Textarea
                rows={3}
                className="min-h-[80px] text-base"
                value={hoursForm.out_of_hours_message}
                onChange={(e) => setHoursForm({ ...hoursForm, out_of_hours_message: e.target.value })}
                placeholder="Mensagem enviada quando receber mensagens fora do horário..."
              />
            </div>
            
            <div className="space-y-3 p-3 rounded bg-muted/50">
              <div className="flex items-center gap-2">
                <Switch
                  checked={hoursForm.welcome_enabled}
                  onCheckedChange={(checked) => setHoursForm({ ...hoursForm, welcome_enabled: checked })}
                />
                <Label className="flex items-center gap-2">
                  <Sun className="w-4 h-4 text-yellow-500" />
                  Mensagem de Boas-vindas
                </Label>
              </div>
              {hoursForm.welcome_enabled && (
                <Textarea
                  rows={3}
                  className="min-h-[80px] text-base"
                  value={hoursForm.welcome_message}
                  onChange={(e) => setHoursForm({ ...hoursForm, welcome_message: e.target.value })}
                  placeholder="Mensagem enviada na primeira mensagem do dia..."
                />
              )}
              <p className="text-xs text-muted-foreground">
                Enviada apenas 1x por dia para cada contato
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setHoursModalOpen(false)}>Cancelar</Button>
            <Button onClick={saveBusinessHours} disabled={saving}>
              {saving ? 'Salvando...' : 'Salvar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset Config Modal */}
      <Dialog open={configModalOpen} onOpenChange={setConfigModalOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Zap className="w-5 h-5 text-yellow-500" />
              Configuração de Reset de Fluxo
            </DialogTitle>
            <DialogDescription>
              Configure uma palavra-chave para resetar o fluxo de um usuário específico
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Conexão WhatsApp *</Label>
              <Select 
                value={configForm.connection_id || 'select'} 
                onValueChange={(v) => {
                  if (v !== 'select') {
                    // Carregar configuração existente para esta conexão
                    const existing = automationConfigs.find(c => c.connection_id === v);
                    if (existing) {
                      setConfigForm({
                        connection_id: v,
                        reset_keywords: existing.reset_keywords || '',
                        reset_message: existing.reset_message || ''
                      });
                    } else {
                      setConfigForm({ ...configForm, connection_id: v });
                    }
                  }
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecione a conexão" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="select" disabled>Selecione...</SelectItem>
                  {connectedConnections.map(conn => (
                    <SelectItem key={conn.id} value={conn.id}>{conn.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Palavra(s)-chave de Reset</Label>
              <Input
                placeholder="finalizar, encerrar, reset (separadas por vírgula)"
                value={configForm.reset_keywords}
                onChange={(e) => setConfigForm({ ...configForm, reset_keywords: e.target.value })}
              />
              <p className="text-xs text-muted-foreground">
                Quando você (admin) enviar essa mensagem para um cliente, o fluxo dele será zerado.
                Ele voltará ao início e responderá às automações principais novamente.
              </p>
            </div>
            
            <div className="space-y-2">
              <Label>Mensagem de Confirmação (opcional)</Label>
              <Textarea
                rows={2}
                className="min-h-[60px] text-base"
                placeholder="Ex: Atendimento finalizado. Obrigado!"
                value={configForm.reset_message}
                onChange={(e) => setConfigForm({ ...configForm, reset_message: e.target.value })}
              />
              <p className="text-xs text-muted-foreground">
                Mensagem enviada automaticamente quando o fluxo for resetado
              </p>
            </div>
            
            {/* Mostrar configurações existentes */}
            {automationConfigs.length > 0 && (
              <div className="pt-3 border-t">
                <p className="text-sm font-medium mb-2">Configurações existentes:</p>
                <div className="space-y-2">
                  {automationConfigs.map(config => (
                    <div key={config.id} className="flex items-center justify-between p-2 rounded bg-muted/50 text-sm">
                      <div>
                        <span className="font-medium">{config.connection_name}</span>
                        <span className="text-muted-foreground ml-2">
                          → &quot;{config.reset_keywords || 'Não configurado'}&quot;
                        </span>
                      </div>
                      <Button 
                        size="sm" 
                        variant="ghost"
                        onClick={() => setConfigForm({
                          connection_id: config.connection_id,
                          reset_keywords: config.reset_keywords || '',
                          reset_message: config.reset_message || ''
                        })}
                      >
                        <Edit className="w-3 h-3" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfigModalOpen(false)}>Cancelar</Button>
            <Button onClick={saveConfig} disabled={saving || !configForm.connection_id}>
              {saving ? 'Salvando...' : 'Salvar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
